/**
 * Фильтр списка проектов нового дизайна (GET /api/projects?f.<поле>=…) — общий модуль сервера и клиента
 * (в клиенте — алиас @projectSearch). Единственная зависимость — общий модуль этапов (shared/stages).
 *
 * Поля контракта — девять; в меню интерфейса их семь: заказчик в меню один и раскладывается на три части
 * (отдел, издательство, редактор), у каждой свои id.
 * Значения — id (person_id, legal_entity_id, edition_id, department_id); для stage — основной этап,
 * для type — код типа; PROJECT_FILTER_NONE — «Не указан» (у всех полей, кроме stage; у заказчика — в edition).
 */
import { PROJECT_STAGES } from '../stages/stages.js';

export const PROJECT_FILTER_FIELDS = [
  'producer',
  'stage',
  'type',
  'author',
  'reader',
  'contractor',
  'department',
  'edition',
  'editor',
] as const;

export type ProjectFilterField = (typeof PROJECT_FILTER_FIELDS)[number];

/** Поля меню фильтра: «заказчик» объединяет три поля из CUSTOMER_PARTS. */
export const PROJECT_FILTER_MENU_FIELDS = [
  'producer',
  'stage',
  'type',
  'author',
  'reader',
  'contractor',
  'customer',
] as const;

export type ProjectFilterMenuField = (typeof PROJECT_FILTER_MENU_FIELDS)[number];

/** Части заказчика — три поля контракта за одним пунктом меню. */
export const CUSTOMER_PARTS = ['department', 'edition', 'editor'] as const;

/** Значение «Не указан» — проекты, у которых поле пустое. */
export const PROJECT_FILTER_NONE = 'none';
export const PROJECT_FILTER_NONE_LABEL = 'Не указан';

/** Сколько значений можно выбрать в одном поле. */
export const PROJECT_FILTER_MAX_VALUES = 20;

export type ProjectFilter = Partial<Record<ProjectFilterField, string[]>>;

/** Вкладки списка: «В работе», «Готовые», «Архив». */
export const PROJECT_LIST_TABS = ['active', 'ready', 'archive'] as const;

export type ProjectListTab = (typeof PROJECT_LIST_TABS)[number];

/** Основные этапы в порядке производства — общая цепочка этапов (клиентский PROJECT_STATUSES — тот же список в обратном порядке). */
export { PROJECT_STAGES };

/** Коды типов проекта (совпадают с PROJECT_TYPES клиента). */
export const PROJECT_TYPE_CODES = ['Common', 'Author', 'Azbooka', 'Podcast', 'Special', 'AudioDrama', 'AI'] as const;

/** Любые 8-4-4-4-12 hex, а не z.uuid(): zod 4 отвергает id с нестандартными версией и вариантом, а Postgres их принимает. */
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Допустимое значение поля — единое правило сервера (400 на остальное) и клиента (чтение сохранённого фильтра):
 * stage — один из PROJECT_STAGES (none нельзя); type — код типа или none; остальные — id или none.
 */
export function isProjectFilterValue(field: ProjectFilterField, value: string): boolean {
  if (field === 'stage') return (PROJECT_STAGES as readonly string[]).includes(value);
  if (field === 'type') return value === PROJECT_FILTER_NONE || (PROJECT_TYPE_CODES as readonly string[]).includes(value);
  return value === PROJECT_FILTER_NONE || GUID_RE.test(value);
}

/** Пары query-параметров `['f.reader', 'id1,id2']`: поля в порядке PROJECT_FILTER_FIELDS, пустые пропущены. */
export function encodeProjectFilter(filter: ProjectFilter): [string, string][] {
  const pairs: [string, string][] = [];
  for (const field of PROJECT_FILTER_FIELDS) {
    const values = filter[field];
    if (!values || values.length === 0) continue;
    // Сортировка даёт стабильный ключ кэша: тот же набор значений — тот же запрос
    pairs.push([`f.${field}`, [...values].sort().join(',')]);
  }
  return pairs;
}

export function isProjectFilterEmpty(filter: ProjectFilter): boolean {
  return PROJECT_FILTER_FIELDS.every((field) => (filter[field]?.length ?? 0) === 0);
}

/** Счётчики вкладок: filtered — с учётом фильтра, total — без него. Ключи — вкладки. */
export type ProjectFilterCounts = { filtered: Record<string, number>; total: Record<string, number> };

/** Вариант значения в меню поля: сколько проектов ему соответствует. group — часть заказчика. */
export type ProjectFilterOption = {
  id: string;
  label: string;
  group?: 'department' | 'edition' | 'editor';
  count: number;
};
